<template>
  <div class="col-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div
        class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
        style="height: 60px;"
      >
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ $t('announcement.edit') }}
            </h6>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <LoadingSpinner v-if="isLoading" :message="$t('announcement.loading')" />

      <!-- Form -->
      <div v-else class="card-body px-0 pb-2">
        <AnnouncementForm
          ref="announcementForm"
          v-if="announcementData"
          :key="announcementData.id"
          :initial-data="announcementData"
          :is-edit-mode="true"
          @submit="updateAnnouncement"
        />

        <div class="d-flex gap-3 mt-3 mx-2">
          <!-- Save -->
          <button
            type="button"
            class="btn btn-primary btn-sm flex-grow-1"
            @click="announcementForm?.submitForm()"
            :disabled="isUpdating"
          >
            <span v-if="isUpdating">
              <i class="fas fa-spinner fa-spin ms-2"></i>
              {{ $t('announcement.saving') }}
            </span>
            <span v-else>
              <i class="fas fa-save ms-2"></i>
              {{ $t('announcement.saveChanges') }}
            </span>
          </button>

          <!-- Cancel -->
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm flex-grow-1"
            @click="navigateBack"
            :disabled="isUpdating"
          >
            <i class="fas fa-times ms-2"></i>
            {{ $t('announcement.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { useStore } from "vuex";
import AnnouncementForm from "@/components/features/announcements/AnnouncementForm.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const toast = useToast();
const store = useStore();

const isLoading = ref(false);
const isUpdating = ref(false);
const announcementForm = ref(null);
const announcementData = ref(null);

const fetchAnnouncement = async () => {
  isLoading.value = true;
  try {
    const response = await store.dispatch(
      "announcements/fetchAnnouncement",
      route.params.id
    );

  const announcement = response.data || response;

    announcementData.value = {
      ...announcement,
      date: announcement.date
        ? new Date(announcement.date).toISOString().slice(0, 16)
        : new Date().toISOString().slice(0, 16),
      status: announcement.status || "active",
    };
  } catch (error) {
    console.error("Error fetching announcement:", error);
    toast.error(t("announcement.fetchError"));
    router.push({ name: "announcements.index" });
  } finally {
    isLoading.value = false;
  }
};


const updateAnnouncement = async (formData) => {
  isUpdating.value = true;
  try {
    await store.dispatch("announcements/updateAnnouncement", {
      id: route.params.id,
      announcementData: formData,
    });

    toast.success(t("announcement.updateSuccess"));
    router.push({ name: "announcements.index" });
  } catch (error) {
    console.error("Error updating announcement:", error);
    toast.error(error?.response?.data?.message || t("announcement.updateError"));
  } finally {
    isUpdating.value = false;
  }
};

const navigateBack = () => {
  router.push({ name: "announcements.index" });
};

onMounted(fetchAnnouncement);

watch(
  () => route.params.id,
  () => {
    fetchAnnouncement();
  }
);
</script>
